import * as React from 'react';
import { Box, IconButton, Typography, type SxProps, type Theme } from '@mui/material';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useTheme } from '@mui/material/styles';

export type ToastType = 'success' | 'alert' | 'error';

export interface PepwiseToastProps {
  type: ToastType;
  title: string;
  message: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  sx?: SxProps<Theme>;
  open?: boolean;
  autoHideDuration?: number;
}

const PepwiseToast: React.FC<PepwiseToastProps> = ({
  type,
  title,
  message,
  icon,
  onClose,
  sx,
  open = true,
  autoHideDuration,
}) => {
  const theme = useTheme();
  const colors = theme.palette;

  const toastStyles: Record<
    ToastType,
    {
      color: string | undefined;
      defaultIcon: React.ReactNode;
    }
  > = {
    success: {
      color: colors.toast.success.title,
      defaultIcon: (
        <CheckCircleOutlineRoundedIcon
          fontSize="medium"
          sx={{ color: colors.toast.success.icon }}
        />
      ),
    },
    alert: {
      color: colors.toast.alert.title,
      defaultIcon: (
        <ErrorOutlineRoundedIcon fontSize="medium" sx={{ color: colors.toast.alert.icon }} />
      ),
    },
    error: {
      color: colors.toast.error.title,
      defaultIcon: <CancelOutlinedIcon fontSize="medium" sx={{ color: colors.toast.error.icon }} />,
    },
  };

  const currentToast = toastStyles[type];

  React.useEffect(() => {
    if (!open || !autoHideDuration || !onClose) return;

    const timer = window.setTimeout(() => {
      onClose();
    }, autoHideDuration);

    return () => {
      window.clearTimeout(timer);
    };
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '480px',
        borderRadius: '4px',
        backgroundColor: `${colors.toast.background} !important`,
        boxShadow:
          '0px -2px 8px rgba(0, 0, 0, 0.04), 0px 6px 18px rgba(0, 0, 0, 0.08), 0px 2px 10px rgba(0, 0, 0, 0.06)',
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 2,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: '2px',
            flexShrink: 0,
          }}
        >
          {icon ?? currentToast.defaultIcon}
        </Box>

        <Box>
          <Typography
            sx={{
              color: currentToast.color,
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '28px',
              mb: 0.5,
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: `${colors.toast.message} !important`,
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '26px',
            }}
          >
            {message}
          </Typography>
        </Box>
      </Box>

      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          mt: '2px',
          p: '4px',
        }}
      >
        <CloseRoundedIcon sx={{ color: `${colors.toast.closeIcon} !important` }} />
      </IconButton>
    </Box>
  );
};

export default PepwiseToast;
